import React, { useState, useEffect, useRef } from 'react';
import SafeImage from '../../Utils/ImageHelper';
import api from '../../Utils/Axios';
import { toast } from 'react-toastify';

const DEFAULT_UNITS = ['pali', 'paiti', 'units', 'bags', 'crates'];

function ProfileTab({
  companyLanguage,
  formData,
  handleChange,
  handleSaveProfile,
  isSaving,
  setActiveTab,
  company
}) {
  const [availableUnits, setAvailableUnits] = useState(DEFAULT_UNITS);
  const [availableItemNames, setAvailableItemNames] = useState([]);

  const [addingUnitForIndex, setAddingUnitForIndex] = useState(null);
  const [newUnitValue, setNewUnitValue] = useState('');
  const [addingUnit, setAddingUnit] = useState(false);
  const newUnitInputRef = useRef(null);

  const [addingItemForIndex, setAddingItemForIndex] = useState(null);
  const [newItemValue, setNewItemValue] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const newItemInputRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('companyToken') || sessionStorage.getItem('companyToken');
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      api.get('/company/units', { headers }).catch(() => ({ data: { units: [] } })),
      api.get('/company/item-names', { headers }).catch(() => ({ data: { itemNames: [] } }))
    ]).then(([unitsRes, itemsRes]) => {
      const merged = [...new Set([...DEFAULT_UNITS, ...(unitsRes.data.units || [])])];
      setAvailableUnits(merged);
      setAvailableItemNames(itemsRes.data.itemNames || []);
    });
  }, []);

  useEffect(() => {
    if (addingUnitForIndex !== null && newUnitInputRef.current)
      setTimeout(() => newUnitInputRef.current?.focus(), 50);
  }, [addingUnitForIndex]);

  useEffect(() => {
    if (addingItemForIndex !== null && newItemInputRef.current)
      setTimeout(() => newItemInputRef.current?.focus(), 50);
  }, [addingItemForIndex]);

  const handleAddNewUnit = async (index) => {
    const trimmed = newUnitValue.trim();
    if (!trimmed) { toast.error(companyLanguage === 'ur' ? 'یونٹ خالی نہیں ہو سکتا' : 'Unit cannot be empty'); return; }
    try {
      setAddingUnit(true);
      const token = localStorage.getItem('companyToken') || sessionStorage.getItem('companyToken');
      const res = await api.post('/company/units', { unit: trimmed }, { headers: { Authorization: `Bearer ${token}` } });
      setAvailableUnits([...new Set([...DEFAULT_UNITS, ...(res.data.units || [])])]);
      if (formData.billItemOptions) {
        const opts = [...formData.billItemOptions];
        opts[index] = { ...opts[index], unit: trimmed };
        handleChange({ target: { name: 'billItemOptions', value: opts } });
      }
      toast.success(companyLanguage === 'ur' ? `"${trimmed}" یونٹ شامل ہو گیا` : `Unit "${trimmed}" added!`);
    } catch { toast.error(companyLanguage === 'ur' ? 'یونٹ شامل نہیں ہو سکا' : 'Failed to add unit'); }
    finally { setAddingUnit(false); setAddingUnitForIndex(null); setNewUnitValue(''); }
  };

  const handleAddNewItem = async (index) => {
    const trimmed = newItemValue.trim();
    if (!trimmed) { toast.error(companyLanguage === 'ur' ? 'چیز کا نام خالی نہیں ہو سکتا' : 'Item name cannot be empty'); return; }
    try {
      setAddingItem(true);
      const token = localStorage.getItem('companyToken') || sessionStorage.getItem('companyToken');
      const res = await api.post('/company/item-names', { itemName: trimmed }, { headers: { Authorization: `Bearer ${token}` } });
      setAvailableItemNames(res.data.itemNames || []);
      if (formData.billItemOptions) {
        const opts = [...formData.billItemOptions];
        opts[index] = { ...opts[index], name: trimmed };
        handleChange({ target: { name: 'billItemOptions', value: opts } });
      }
      toast.success(companyLanguage === 'ur' ? `"${trimmed}" شامل ہو گیا` : `"${trimmed}" added!`);
    } catch { toast.error(companyLanguage === 'ur' ? 'چیز کا نام شامل نہیں ہو سکا' : 'Failed to add item name'); }
    finally { setAddingItem(false); setAddingItemForIndex(null); setNewItemValue(''); }
  };

  const urduLabels = { pali: 'پلی', paiti: 'پیتی', units: 'یونٹس', bags: 'بوریاں', crates: 'کریٹس' };
  const urduStyle = companyLanguage === 'ur'
    ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {};
  const urduClass = companyLanguage === 'ur' ? 'font-urdu' : '';

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header Card */}
      <div className="bg-gradient-to-r from-brand-header to-emerald-800 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div>
            <h2 className={`text-2xl font-bold ${urduClass}`} style={urduStyle}>
              {companyLanguage === 'ur' ? 'پروفائل کی ترتیبات' : 'Company Profile Settings'}
            </h2>
            <p className="text-white/80 text-sm mt-1">
              {companyLanguage === 'ur' ? 'اپنی کمپنی کی معلومات اپ ڈیٹ کریں' : 'Update your company information'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSaveProfile} className="space-y-6">
        {/* Account Information */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-brand-card/60">
          <h3 className={`text-lg font-bold text-brand-header mb-6 flex items-center gap-2 ${urduClass}`} style={urduStyle}>
            <svg className="w-5 h-5 text-brand-header" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {companyLanguage === 'ur' ? 'اکاؤنٹ کی معلومات' : 'Account Information'}
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2 ${urduClass}`} style={urduStyle}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {companyLanguage === 'ur' ? 'یوزرنیم' : 'Username'}
              </label>
              <div className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg text-gray-700">{company?.username || '-'}</div>
            </div>
            <div>
              <label className={`block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2 ${urduClass}`} style={urduStyle}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {companyLanguage === 'ur' ? 'ای میل' : 'Email'}
              </label>
              <div className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg text-gray-700">{company?.email || '-'}</div>
            </div>
          </div>
        </div>

        {/* Main Info */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-brand-card/60">
          <h3 className={`text-lg font-bold text-brand-header mb-6 flex items-center gap-2 ${urduClass}`} style={urduStyle}>
            <svg className="w-5 h-5 text-brand-header" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            {companyLanguage === 'ur' ? 'بنیادی معلومات' : 'Basic Information'}
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-semibold text-brand-header mb-2 flex items-center gap-2 ${urduClass}`} style={urduStyle}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                {companyLanguage === 'ur' ? 'دکان کا نام *' : 'Shop Name *'}
              </label>
              <input type="text" name="shopName" value={formData.shopName} onChange={handleChange} required
                className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all ${urduClass}`}
                style={urduStyle} placeholder={companyLanguage === 'ur' ? 'دکان کا نام درج کریں' : 'Enter shop name'} />
            </div>

            <div className="md:col-span-2">
              <label className={`block text-sm font-semibold text-brand-header mb-2 flex items-center gap-2 ${urduClass}`} style={urduStyle}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {companyLanguage === 'ur' ? 'رابطہ نمبر' : 'Contact Numbers'}
              </label>
              <div className="space-y-3">
                {formData.contacts && formData.contacts.length > 0 ? (
                  formData.contacts.map((contact, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <input type="text" value={contact.name || ''}
                        onChange={(e) => { const nc = [...formData.contacts]; nc[index] = { ...nc[index], name: e.target.value }; handleChange({ target: { name: 'contacts', value: nc } }); }}
                        className={`flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all ${urduClass}`}
                        style={urduStyle} placeholder={companyLanguage === 'ur' ? 'نام' : 'Name'} />
                      <input type="tel" value={contact.number || ''}
                        onChange={(e) => { const nc = [...formData.contacts]; nc[index] = { ...nc[index], number: e.target.value }; handleChange({ target: { name: 'contacts', value: nc } }); }}
                        className={`flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all ${urduClass}`}
                        style={urduStyle} placeholder={companyLanguage === 'ur' ? 'رابطہ نمبر' : 'Contact Number'} />
                      <button type="button" onClick={() => { const nc = formData.contacts.filter((_, i) => i !== index); handleChange({ target: { name: 'contacts', value: nc } }); }}
                        className="px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all flex items-center justify-center">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  ))
                ) : (
                  <p className={`text-sm text-gray-500 ${urduClass}`} style={urduStyle}>
                    {companyLanguage === 'ur' ? 'کوئی رابطہ نمبر شامل نہیں' : 'No contact numbers added'}
                  </p>
                )}
                <button type="button" onClick={() => { const nc = [...(formData.contacts || []), { name: '', number: '' }]; handleChange({ target: { name: 'contacts', value: nc } }); }}
                  className="w-full px-4 py-2 bg-brand-header hover:bg-emerald-800 text-white rounded-lg transition-all flex items-center justify-center gap-2 font-semibold">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  <span className={urduClass} style={urduStyle}>{companyLanguage === 'ur' ? 'رابطہ نمبر شامل کریں' : 'Add Contact Number'}</span>
                </button>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className={`block text-sm font-semibold text-brand-header mb-2 flex items-center gap-2 ${urduClass}`} style={urduStyle}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {companyLanguage === 'ur' ? 'پتہ *' : 'Address *'}
              </label>
              <textarea name="address" value={formData.address} onChange={handleChange} required rows="3"
                className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all resize-none ${urduClass}`}
                style={urduStyle} placeholder={companyLanguage === 'ur' ? 'کمپنی کا پتہ درج کریں' : 'Enter company address'} />
            </div>
          </div>
        </div>

        {/* Logo */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-brand-card/60">
          <h3 className={`text-lg font-bold text-brand-header mb-6 flex items-center gap-2 ${urduClass}`} style={urduStyle}>
            <svg className="w-5 h-5 text-brand-header" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {companyLanguage === 'ur' ? 'لوگو' : 'Company Logo'}
          </h3>
          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-semibold text-brand-header mb-2 flex items-center gap-2 ${urduClass}`} style={urduStyle}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                {companyLanguage === 'ur' ? 'لوگو یو آر ایل' : 'Logo URL'}
              </label>
              <input type="url" name="logo" value={formData.logo} onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all"
                placeholder="https://example.com/logobest.png" />
              <p className={`text-xs text-gray-500 mt-1 ${urduClass}`} style={urduStyle}>
                {companyLanguage === 'ur'
                  ? 'نوٹ: تصویر کا یو آر ایل مکمل ہونا چاہیے (مثال: https://example.com/image.png). اگر تصویر نہیں لوڈ ہو رہی تو image hosting service استعمال کریں جو CORS allow کرتی ہو (مثلاً Imgur, Cloudinary)'
                  : 'Note: Image URL must be complete (e.g., https://example.com/image.png). If image fails to load, use an image hosting service that allows CORS (e.g., Imgur, Cloudinary)'}
              </p>
            </div>
            {formData.logo && (
              <div className="bg-brand-background rounded-lg p-6 border-2 border-dashed border-brand-card">
                <p className={`text-sm font-semibold text-brand-header mb-4 ${urduClass}`} style={urduStyle}>{companyLanguage === 'ur' ? 'پیش منظر:' : 'Preview:'}</p>
                <div className="flex items-center justify-center bg-white rounded-lg p-4 border border-brand-card shadow-sm relative">
                  <SafeImage src={formData.logo} alt="Logo preview" className="max-h-32 max-w-32 object-contain"
                    onError={(e) => { e.target.style.display = 'none'; const d = e.target.nextElementSibling; if (d) { d.classList.remove('hidden'); d.classList.add('flex'); } }}
                    onLoad={(e) => { e.target.style.display = 'block'; const d = e.target.nextElementSibling; if (d) { d.classList.add('hidden'); d.classList.remove('flex'); } }} />
                  <div className="hidden items-center justify-center h-32 w-32 text-gray-400 flex-col gap-2">
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    <p className={`text-xs text-gray-500 text-center ${urduClass}`} style={urduStyle}>{companyLanguage === 'ur' ? 'تصویر لوڈ نہیں ہو سکی' : 'Image failed to load'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bill Settings Card (if billItemOptions exist) */}
        {formData.billItemOptions !== undefined && (
          <div className="bg-white rounded-xl shadow-lg p-6 border border-brand-card/60">
            <h3 className={`text-lg font-bold text-brand-header mb-6 flex items-center gap-2 ${urduClass}`} style={urduStyle}>
              <svg className="w-5 h-5 text-brand-header" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 00-2 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              {companyLanguage === 'ur' ? 'بل فارم کی ترتیبات' : 'Bill Form Settings'}
            </h3>
            <div className="space-y-4">
              <p className={`text-sm text-gray-600 mb-4 ${urduClass}`} style={urduStyle}>
                {companyLanguage === 'ur' ? 'بل بناتے وقت اشیاء (Items) اور ان کے یونٹس (Units) کے آپشنز یہاں سے سیٹ کریں۔' : 'Set options for items and their units to be used during bill creation.'}
              </p>
              <div className="space-y-3">
                {formData.billItemOptions && formData.billItemOptions.length > 0 ? (
                  formData.billItemOptions.map((option, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      {/* Item Name */}
                      <div className="flex-1">
                        {addingItemForIndex === index ? (
                          <div className="flex gap-2">
                            <input ref={newItemInputRef} type="text" value={newItemValue} onChange={(e) => setNewItemValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNewItem(index); } if (e.key === 'Escape') { setAddingItemForIndex(null); setNewItemValue(''); } }}
                              className={`flex-1 px-3 py-3 border-2 border-emerald-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all ${urduClass}`} style={urduStyle}
                              placeholder={companyLanguage === 'ur' ? 'نئی چیز لکھیں...' : 'Type new item...'} />
                            <button type="button" onClick={() => handleAddNewItem(index)} disabled={addingItem} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold transition-all disabled:opacity-50">{addingItem ? '...' : '✓'}</button>
                            <button type="button" onClick={() => { setAddingItemForIndex(null); setNewItemValue(''); }} className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-bold transition-all">✕</button>
                          </div>
                        ) : (
                          <select value={option.name || ''}
                            onChange={(e) => { const val = e.target.value; if (val === '__other__') { setAddingItemForIndex(index); setNewItemValue(''); } else { const opts = [...formData.billItemOptions]; opts[index] = { ...opts[index], name: val }; handleChange({ target: { name: 'billItemOptions', value: opts } }); } }}
                            className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all bg-white ${urduClass}`} style={urduStyle}>
                            <option value="">{companyLanguage === 'ur' ? '-- چیز منتخب کریں --' : '-- Select Item --'}</option>
                            {availableItemNames.map((n, i) => <option key={i} value={n}>{n}</option>)}
                            <option value="__other__">{companyLanguage === 'ur' ? '+ دوسرا (نیا شامل کریں)' : '+ Other (Add new)...'}</option>
                          </select>
                        )}
                      </div>
                      {/* Unit */}
                      <div className="flex-1">
                        {addingUnitForIndex === index ? (
                          <div className="flex gap-2">
                            <input ref={newUnitInputRef} type="text" value={newUnitValue} onChange={(e) => setNewUnitValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNewUnit(index); } if (e.key === 'Escape') { setAddingUnitForIndex(null); setNewUnitValue(''); } }}
                              className={`flex-1 px-3 py-3 border-2 border-emerald-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all ${urduClass}`} style={urduStyle}
                              placeholder={companyLanguage === 'ur' ? 'نیا یونٹ لکھیں...' : 'Type new unit...'} />
                            <button type="button" onClick={() => handleAddNewUnit(index)} disabled={addingUnit} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold transition-all disabled:opacity-50">{addingUnit ? '...' : '✓'}</button>
                            <button type="button" onClick={() => { setAddingUnitForIndex(null); setNewUnitValue(''); }} className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-bold transition-all">✕</button>
                          </div>
                        ) : (
                          <select value={option.unit || ''}
                            onChange={(e) => { const val = e.target.value; if (val === '__other__') { setAddingUnitForIndex(index); setNewUnitValue(''); } else { const opts = [...formData.billItemOptions]; opts[index] = { ...opts[index], unit: val }; handleChange({ target: { name: 'billItemOptions', value: opts } }); } }}
                            className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all bg-white ${urduClass}`} style={urduStyle}>
                            <option value="">{companyLanguage === 'ur' ? '-- یونٹ منتخب کریں --' : '-- Select Unit --'}</option>
                            {availableUnits.map((u, i) => <option key={i} value={u}>{companyLanguage === 'ur' ? (urduLabels[u] || u) : u}</option>)}
                            <option value="__other__">{companyLanguage === 'ur' ? '+ دوسرا (نیا شامل کریں)' : '+ Other (Add new)...'}</option>
                          </select>
                        )}
                      </div>
                      <button type="button" onClick={() => { const opts = formData.billItemOptions.filter((_, i) => i !== index); handleChange({ target: { name: 'billItemOptions', value: opts } }); }}
                        className="px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all flex items-center justify-center">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  ))
                ) : (
                  <p className={`text-sm text-gray-500 text-center py-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg ${urduClass}`} style={urduStyle}>
                    {companyLanguage === 'ur' ? 'کوئی آپشن شامل نہیں کیا گیا' : 'No options added yet'}
                  </p>
                )}
                <button type="button" onClick={() => { const opts = [...(formData.billItemOptions || []), { name: '', unit: '' }]; handleChange({ target: { name: 'billItemOptions', value: opts } }); }}
                  className="w-full px-4 py-3 bg-emerald-50 border-2 border-dashed border-emerald-300 text-emerald-700 hover:bg-emerald-100 rounded-lg transition-all flex items-center justify-center gap-2 font-bold">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  <span className={urduClass} style={urduStyle}>{companyLanguage === 'ur' ? 'نیا آپشن شامل کریں' : 'Add New Option'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-4">
          <button type="button" onClick={() => setActiveTab("overview")}
            className={`px-6 py-3 border-2 border-brand-card rounded-lg text-gray-700 hover:bg-brand-background hover:border-brand-header transition-all transform hover:scale-105 font-semibold ${urduClass}`} style={urduStyle}>
            {companyLanguage === 'ur' ? 'منسوخ' : 'Cancel'}
          </button>
          <button type="submit" disabled={isSaving}
            className="bg-brand-header hover:bg-emerald-800 text-white font-semibold px-8 py-3 rounded-lg transition-all transform hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {isSaving ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className={urduClass} style={urduStyle}>{companyLanguage === 'ur' ? 'بچایا جا رہا ہے...' : 'Saving...'}</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className={urduClass} style={urduStyle}>{companyLanguage === 'ur' ? 'بچائیں' : 'Save Profile'}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProfileTab;
